/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import 'date-fns';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import EyeIcon from '@material-ui/icons/RemoveRedEyeTwoTone';
import Button from '@material-ui/core/Button';

import { PergoTextField } from 'components/PergoTextField/PergoTextField';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';

import { JobContext } from 'context/jobContextContainer';
import { CallContext } from 'context/callContextContainer';
import { jobsService } from 'services/jobsService';

import { logout, isPergoManager } from 'services/loginService';

const moment = require('moment-timezone');

const JobTable = ({ data, editJob, confirmReservation, isCallCenter }) => {
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [assignDriverConfirm, setAssignDriverConfirm] = useState(false);
  const [tagMismatchConfirm, setTagMismatchConfirm] = useState(false);
  const [searchValues, setSearchValues] = useState({
    fname: '',
    lname: '',
    phone: '',
  });
  const [showBDUnpaid, setShowBDUnpaid] = useState(false);
  const { activeDriverList, setNoShowDialogInfo, smartMatchSuggestions, setSmartMatchModalInfo } = useContext(JobContext);

  const smSuggestionFor = id => (smartMatchSuggestions || []).find(s => String(s.job_id) === String(id));
  const { siteSetting } = useContext(CallContext);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);

  const paymentType = ['Cash', 'Card', 'Voucher', 'EHail Mobile', 'Karhoo', 'Stored Card', 'Webapp Credit'];

  const generateCommonPickupDropoffTimeCell = (row, type) => {
    if (type === 'pickup') {
      const pickup_time = moment(row.pickup_time).isValid()
        ? moment
          .utc(row.pickup_time)
          .tz(siteSetting().timezone)
          .format('hh:mm A')
        : '';
      return <TableCell align="center" width="120px">{pickup_time}</TableCell>
    }
    if (type === 'dropoff') {
      const dropoff_time = moment(row.dropoff_time).isValid()
        ? moment
          .utc(row.dropoff_time)
          .tz(siteSetting().timezone)
          .format('hh:mm A')
        : '';
      return <TableCell align="center" width="120px">{dropoff_time}</TableCell>
    }
    return null;
  };

  const genertePickupDropoffTimeCell = (row, type) => {
    const settings = siteSetting().settings || {};
    if (settings.usage_live_eta !== 'on') {
      return generateCommonPickupDropoffTimeCell(row, type);
    }

    let eta_info = null;
    // Status 1 = Dispatched: ETA is computed at assignment, so show it from that point on
    // (not only after the driver Accepts = 2).
    if ((row.status == 1 || row.status == 2 || row.status == 4 || row.status == 5) && row.eta_info) {
      eta_info = JSON.parse(row.eta_info);
    }
    if (type === 'pickup') {
      if (row.status > 4) {
        return generateCommonPickupDropoffTimeCell(row, type);
      }
      const reservation_time = moment(row.reservation_time).isValid()
        ? moment.utc(row.reservation_time).tz(siteSetting().timezone).format('hh:mm A')
        : '-';
      const eta_pickup_time = eta_info && moment(eta_info.pickup.time).isValid()
        ? moment.utc(eta_info.pickup.time).tz(siteSetting().timezone).format('hh:mm A')
        : '-';
      const eta_pickup_on_schedule = moment(row.reservation_time).isValid() && eta_info ? moment(row.reservation_time).isAfter(eta_info.pickup.time) : true;
      const color = eta_pickup_on_schedule ? 'black' : 'red';
      return (
        <TableCell align="center" width="120px">
          <div>
            <span style={{ whiteSpace: 'nowrap' }}><b style={{ color: color }}>{eta_pickup_on_schedule ? 'On schedule' : 'Not on schedule'}</b></span><br />
            <span style={{ display: 'flex', alignItems: 'center' }}><b style={{ color: '#009000' }}>Sch:</b> <span style={{ whiteSpace: 'nowrap', width: '100%' }}>{reservation_time}</span></span>
            <span style={{ display: 'flex', alignItems: 'center' }}><b style={{ color: 'blue' }}>ETA:</b> <span style={{ whiteSpace: 'nowrap', width: '100%', color }}>{eta_pickup_time}</span></span>
          </div>
        </TableCell>
      );
    } else if (type === 'dropoff') {
      if (row.status > 5) {
        return generateCommonPickupDropoffTimeCell(row, type);
      }
      const is_real_reservation_end_time = row.reservation_end_time != row.reservation_time;
      const reservation_end_time = is_real_reservation_end_time && moment(row.reservation_end_time).isValid()
        ? moment.utc(row.reservation_end_time).tz(siteSetting().timezone).format('hh:mm A')
        : '-';
      const eta_dropoff_time = eta_info && moment(eta_info.dropoff.time).isValid()
        ? moment.utc(eta_info.dropoff.time).tz(siteSetting().timezone).format('hh:mm A')
        : '-';
      const eta_dropoff_on_schedule = is_real_reservation_end_time && moment(row.reservation_end_time).isValid() && eta_info ? moment(row.reservation_end_time).isAfter(eta_info.dropoff.time) : true;
      const color = eta_dropoff_on_schedule ? 'black' : 'red';
      return (
        <TableCell align="center" width="120px">
          <div>
            <span style={{ whiteSpace: 'nowrap' }}><b style={{ color: color }}>{eta_dropoff_on_schedule ? 'On schedule' : 'Not on schedule'}</b></span><br />
            <span style={{ display: 'flex', alignItems: 'center' }}><b style={{ color: '#009000' }}>Sch:</b> <span style={{ whiteSpace: 'nowrap', width: '100%' }}>{reservation_end_time}</span></span>
            <span style={{ display: 'flex', alignItems: 'center' }}><b style={{ color: 'blue' }}>ETA:</b> <span style={{ whiteSpace: 'nowrap', width: '100%', color }}>{eta_dropoff_time}</span></span>
          </div>
        </TableCell>
      );
    }
    return null;
  };

  const jobTableRowFormat = row => {
    if (!siteSetting()) {
      return null;
    }

    const call_time = moment(row.call_time).isValid()
      ? moment
        .utc(row.call_time)
        ?.tz(siteSetting().timezone)
        ?.format('MM/DD/YYYY hh:mm A')
      : '';
    const pick_up = row.pickup
      ? [row.pickup.location_name, row.pickup.addr1, row.pickup.city, row.pickup.state]
        .filter(str => str?.length > 0)
        .join(', ')
      : row.scheduled_start_addr || row.start_addr;
    const drop_off = row.dropoff
      ? [row.dropoff.location_name, row.dropoff.addr1, row.dropoff.city, row.dropoff.state]
        .filter(str => str?.length > 0)
        .join(', ')
      : row.scheduled_end_addr || row.end_addr;
    let { dispatch_time } = row;
    let status = '';
    let status_attr = '';
    row.job_status = parseInt(row.status);
    row.status = parseInt(row.status);
    row.job_type = parseInt(row.job_type);

    switch (row.status) {
      case 1:
        status = row.job_type === 2 ? 'Reservation/Dispatched' : 'Dispatched';
        status_attr = 'dispatched';
        break;
      case 2:
        status = 'Accepted';
        status_attr = 'accepted';
        break;
      case 3:
        status = 'Declined';
        status_attr = 'declined';
        break;
      case 4:
        status = '@Pickup';
        status_attr = 'accepted';
        break;
      case 5:
        status = 'Picked Up';
        status_attr = 'pickup';
        break;
      case 6:
        status = 'Drop-Off';
        status_attr = 'dropoff';
        break;
      case 7:
        status = 'Pay';
        status_attr = 'pay';
        break;
      case 8:
        status = 'Complete';
        status_attr = 'complete';
        break;
      case 9:
        status = 'No Show';
        status_attr = 'cancel';
        break;
      case 10:
        status = 'Cancelled';
        status_attr = 'cancel';
        break;
      case 11:
        status = 'Backlog';
        status_attr = 'backlog';
        break;
      default:
        status = 'CFS';
        status_attr = 'cfs';
        break;
    }

    if (row.status === 0) {
      switch (row.job_type) {
        case 2:
          dispatch_time = row.reservation_time;
          status = 'Reservation';
          if (row.driver_id <= 0 && row.reservation_confirmed != 0 && row.reservation_confirmed != 3) {
            status_attr = 'no-driver';
            const diffTime = moment.utc(dispatch_time).diff(moment(), 'minutes');
            if (diffTime >= 20) {
              status_attr += '-green';
            } else if (diffTime >= 10) {
              status_attr += '-orange';
            } else {
              status_attr += '-red';
            }
          }
          break;
        case 3:
          status = 'E-Hail';
          break;
        case 4:
          status += ' (E-Hail)';
          break;
        default:
          break;
      }
    }

    dispatch_time = moment(dispatch_time).isValid()
      ? moment
        .utc(dispatch_time)
        ?.tz(siteSetting().timezone)
        ?.format(row.willcall_status == '1' ? 'MM/DD/YYYY' : 'MM/DD/YYYY hh:mm A')
      : '';
    const distance = parseFloat(row.distance).toFixed(2);
    const payment_type = paymentType[row.tender_type];

    return {
      ...row,
      call_time,
      pick_up,
      drop_off,
      dispatch_time,
      distance,
      payment_type,
      status_text: status,
      status_attr,
      reservation_confirmed: row.reservation_confirmed,
    };
  };

  useEffect(() => {
    setTableData([...data]);
  }, [data]);

  const handleEditJob = job_id => {
    editJob(job_id);
  };

  const handleConfirmClick = (jobInfo, confirm) => {
    if (jobInfo) {
      confirmReservation([jobInfo], confirm);
    }
  };

  const onClickDriverAssign = (jobInfo, driverId, index) => {
    if (jobInfo.driver_id == driverId) {
      return;
    }

    if (parseFloat(jobInfo.booking_deposit_amount) > 0 && jobInfo.booking_deposit_status == 0) {
      setShowBDUnpaid(true);
      return;
    }

    const selDriver = activeDriverList.filter(item => item.id === driverId);
    if (selDriver?.length > 0) {
      setAssignDriverConfirm({
        jobId: jobInfo.id,
        driverId,
        driverLabel: selDriver[0].name,
        vehicleId: selDriver[0].vehicle_id,
      });
    }
  };

  const handleDriverAssign = async (assignInfo, tagOverride = false) => {
    const { jobId, driverId, vehicleId } = assignInfo;
    setAssignDriverConfirm(false);
    setTagMismatchConfirm(false);
    showCircularProgress('Loading');
    const res = await jobsService.dispatch_driver_to_job(jobId, driverId, vehicleId, tagOverride);
    hideCircularProgress();
    if (res && res?.data?.status === 'SUCCESS') {
      // updateTable();
    } else if (res && res?.data?.status === 'FAIL' && res.data?.data?.code === 'TAG_MISMATCH') {
      // Warn-but-allow: let the dispatcher confirm and re-send with an override.
      setTagMismatchConfirm({ ...assignInfo, msg: res.data.data.msg });
    } else if (res && res?.data?.status === 'FAIL') {
      showNotification(`Sorry, Failed to dispatch driver: ${res.data?.data?.msg}`, 'error');
    }
  };

  const handleNoShowClick = item => {
    if (item?.noshow_information) {
      const noShowInfo = JSON.parse(item.noshow_information);
      setNoShowDialogInfo({
        id: item.id,
        noShowInfo,
        pickupInfo: {
          addr: item.start_addr,
          lat: item.start_lat,
          lon: item.start_lon,
          time: item.reservation_time,
        },
      });
    }
  };

  const onSearchChange = (id, newValue) => {
    setSearchValues({
      ...searchValues,
      [id]: newValue,
    });
  };

  const closeBDUnpaid = () => {
    setShowBDUnpaid(false);
  };

  useEffect(() => {
    const filtered = tableData
      .filter(item => {
        const pname = item.passenger_name?.split(' ');
        if (searchValues?.fname?.length && !pname?.[0]?.toLowerCase().includes(searchValues.fname.toLowerCase())) {
          return false;
        }
        if (searchValues?.lname?.length && !pname?.[1]?.toLowerCase().includes(searchValues.lname.toLowerCase())) {
          return false;
        }
        if (
          searchValues?.phone?.length &&
          !item.passenger_phone?.toLowerCase().includes(searchValues.phone.toLowerCase())
        ) {
          return false;
        }
        return true;
      })
      .map(item => jobTableRowFormat(item))
      .filter(item => item !== null);
    setFilteredTableData(filtered);
  }, [tableData, searchValues?.fname, searchValues?.lname, searchValues?.phone]);

  const dispatchDriverList = (list, item) => {
    const tmpList = [...(list || [])];
    if (item.driver_id > 0 && !tmpList?.some(d => d.id == item.driver_id)) {
      tmpList.push({
        id: item.driver_id,
        name: item.driver_name,
      });
    }
    return tmpList;
  };

  return (
    <TableContainer>
      <TableSearch>
        <TableSearchGroup values={searchValues} onValueChange={onSearchChange} />
      </TableSearch>
      <Table aria-label="job table">
        <TableHead>
          <TableRow>
            <TableCell align="center">Job #</TableCell>
            <TableCell align="center">Driver</TableCell>
            <TableCell align="center">Updated Time</TableCell>
            <TableCell align="center">Passenger</TableCell>
            <TableCell align="center">Pickup</TableCell>
            <TableCell align="center">Drop Off</TableCell>
            <TableCell align="center">Dispatch Time</TableCell>
            <TableCell align="center">Car #</TableCell>
            <TableCell align="center">Distance</TableCell>
            <TableCell align="center">Payment Type</TableCell>
            <TableCell align="center">Fare</TableCell>
            <TableCell align="center">Pickup Time</TableCell>
            <TableCell align="center">Drop Off Time</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Cancel</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTableData.map((item, index) => (
            <TableRow hover key={`${item.id}_${index}`} className={`job-status-${item.status_attr}`}>
              <TableCell align="center" style={{ cursor: 'pointer' }} onClick={() => handleEditJob(item.id)}>
                <div>{item.id}</div>
                {smSuggestionFor(item.id) && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={e => {
                      e.stopPropagation();
                      setSmartMatchModalInfo(smSuggestionFor(item.id));
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        setSmartMatchModalInfo(smSuggestionFor(item.id));
                      }
                    }}
                    style={{
                      marginTop: 4,
                      cursor: 'pointer',
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: '#fff',
                      background: smSuggestionFor(item.id).trigger_type === 'late' ? '#ff8a3d' : '#2f6df6',
                      borderRadius: 10,
                      padding: '2px 6px',
                      display: 'inline-block',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {smSuggestionFor(item.id).trigger_type === 'late' ? 'LATE · MATCH' : 'DUE · MATCH'}
                  </div>
                )}
              </TableCell>
              <TableCell align="center">
                {item.willcall_status == '1' ? (
                  <></>
                ) : isCallCenter || item.job_status >= 9 ? (
                  item.driver_name
                ) : (
                  <Select
                    value={parseInt(item.driver_id) || -1}
                    onChange={e => onClickDriverAssign(item, e.target.value, index)}
                    style={{ width: '100%', fontSize: '13px', textAlign: 'left' }}
                  >
                    <MenuItem value="-1">&nbsp;</MenuItem>
                    {activeDriverList?.length > 0 &&
                      dispatchDriverList(activeDriverList, item).map((d, index1) => (
                        <MenuItem value={d.id} key={index1}>
                          {d.name}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              </TableCell>
              <TableCell align="center">{item.call_time}</TableCell>
              <TableCell align="center">{item.passenger_name}</TableCell>
              <TableCell align="center">{item.pick_up}</TableCell>
              <TableCell align="center">{item.drop_off}</TableCell>
              <TableCell align="center">
                {item.dispatch_time}
                {item.willcall_status == '1' ? <span style={{ color: 'red' }}>&nbsp;(Will Call)</span> : ''}
              </TableCell>
              <TableCell align="center">{item.vehicle_no}</TableCell>
              <TableCell align="center">{item.distance}</TableCell>
              <TableCell align="center">
                {item.payment_type}
                {parseFloat(item.booking_deposit_amount) > 0 && item.booking_deposit_status == 0 ? (
                  <>
                    <br />
                    <span style={{ color: 'red' }}>(Balance Due)</span>
                  </>
                ) : (
                  <></>
                )}
              </TableCell>
              <TableCell align="center">{item.fare}</TableCell>
              {genertePickupDropoffTimeCell(item, 'pickup')}
              {genertePickupDropoffTimeCell(item, 'dropoff')}
              {item.status_text === 'Reservation' && (item.reservation_confirmed == 0 || item.reservation_confirmed == 3) ? (
                <TableCell align="center" className="text-warning">
                  Confirmed
                  <br />
                  Reservation?
                </TableCell>
              ) : (
                <TableCell align="center">
                  {item.status_text}
                  {/* {item.driver_id <= 0 && item.status < 2 ? (
                    <>
                      <br />
                      No Driver's Assigned
                    </>
                  ) : (
                    ''
                  )} */}
                  {item.status_text === 'No Show' && (
                    <EyeIcon
                      color="primary"
                      style={{ fontSize: 20, marginLeft: 5, marginBottom: 3, cursor: 'pointer' }}
                      onClick={() => handleNoShowClick(tableData[index])}
                    />
                  )}
                </TableCell>
              )}
              <TableCell align="center">
                {item.status_text === 'Reservation' && (item.reservation_confirmed == 0 || item.reservation_confirmed == 3) && (
                  <Button
                    className="job-table--action-icon color-success m-1"
                    variant="contained"
                    onClick={() => handleConfirmClick(item, 'confirm')}
                  >
                    <CheckIcon fontSize="small" />
                  </Button>
                )}

                <Button
                  className="job-table--action-icon m-1"
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    handleConfirmClick(
                      item,
                      item.status_text === 'Reservation' &&
                        (item.reservation_confirmed == 0 || item.reservation_confirmed == 3)
                        ? 'decline'
                        : 'cancel'
                    )
                  }
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!assignDriverConfirm} onClose={() => setAssignDriverConfirm(false)}>
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Do you really want to assign ${assignDriverConfirm?.driverLabel} driver to Job #${assignDriverConfirm?.jobId}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDriverAssign(assignDriverConfirm)} color="primary" autoFocus>
            Yes
          </Button>
          <Button onClick={() => setAssignDriverConfirm(false)} color="primary" autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!tagMismatchConfirm} onClose={() => setTagMismatchConfirm(false)}>
        <DialogTitle id="tag-mismatch-title">Tag requirement not met</DialogTitle>
        <DialogContent>
          <DialogContentText id="tag-mismatch-description">
            {`This driver/vehicle does not meet the job's tag requirements (${tagMismatchConfirm?.msg}). Dispatch anyway?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDriverAssign(tagMismatchConfirm, true)} color="primary" autoFocus>
            Dispatch anyway
          </Button>
          <Button onClick={() => setTagMismatchConfirm(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showBDUnpaid} onClose={closeBDUnpaid}>
        <DialogTitle id="alert-dialog-title">Sorry</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Driver cannot be assigned since the booking deposit has not been paid.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBDUnpaid} color="primary" autoFocus>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default JobTable;

// eslint-disable-next-line arrow-body-style
const TableSearchGroup = ({ values, onValueChange }) => {
  return (
    <div className="reservation-table-search--container">
      <PergoTextField id="fname" values={values} handleChange={onValueChange} label="First Name" />
      <PergoTextField id="lname" values={values} handleChange={onValueChange} label="Last Name" />
      <PergoTextField id="phone" values={values} handleChange={onValueChange} label="Phone" />
    </div>
  );
};

const TableSearch = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  .reservation-table-search--container {
    padding-top: 5px;

    .MuiTextField-root {
      margin-right: 10px;
    }

    .MuiInputLabel-outlined {
      font-size: 14px;
      transform: translate(14px, 12px) scale(1);
      background: transparent;
      padding: 0 3px;

      &.MuiInputLabel-shrink {
        transform: translate(14px, -5px) scale(0.75);
      }
    }

    .MuiOutlinedInput-input {
      padding: 10px;
      font-size: 14px;
      height: auto;
    }
  }
`;
